/**
 * Rate Guard cache + telemetry helpers.
 *
 * - `buildSpotRateKey` — single canonical React Query key shape used by
 *   useSpotRate, useMyLoads, useMyTrucks. Normalizes miles into 10-mi buckets
 *   and sorts excludeLoadIds so call ordering doesn't fragment the cache.
 * - `fetchSpotRateCached` — `queryClient.fetchQuery` wrapper so non-component
 *   call sites (fire-and-forget enrichment loops) participate in the same
 *   cache as `useSpotRate`.
 * - `emitRateGuardTelemetry` — structured `console.debug` event used to
 *   measure L1–L4 tier hit rates. Dev-on by default; in production it is
 *   gated by `localStorage.rateGuardTelemetry === '1'`.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  computeSpotRate,
  makeLaneKey,
  type SpotRateInput,
  type SpotRateOptions,
  type SpotRateResult,
  type SpotTier,
} from '@/lib/spotRateEngine';

export const SPOT_RATE_STALE_MS = 5 * 60_000;   // 5 min
export const SPOT_RATE_GC_MS    = 30 * 60_000;  // 30 min

export function buildSpotRateKey(i: SpotRateInput): QueryKey {
  const milesBucket = Math.round((i.laneMiles || 0) / 10) * 10;
  const hasCityState = !!(i.originCity && i.originState && i.destCity && i.destState);
  const laneKey = hasCityState
    ? makeLaneKey(i.originCity!, i.originState!, i.destCity!, i.destState!, i.equipmentType)
    : `geo:${i.originLat.toFixed(2)},${i.originLon.toFixed(2)}>${i.destLat.toFixed(2)},${i.destLon.toFixed(2)}:${i.equipmentType}`;
  const excludeKey = i.excludeLoadIds?.length
    ? i.excludeLoadIds.slice().sort().join(',')
    : '';
  return ['spot-rate', laneKey, milesBucket, excludeKey];
}

export async function fetchSpotRateCached(
  qc: QueryClient,
  input: SpotRateInput,
  opts?: SpotRateOptions,
): Promise<SpotRateResult | null> {
  return qc.fetchQuery({
    queryKey: buildSpotRateKey(input),
    queryFn: () => computeSpotRate(input, opts),
    staleTime: SPOT_RATE_STALE_MS,
    gcTime: SPOT_RATE_GC_MS,
  });
}

interface RateGuardEvent {
  tier_resolved: SpotTier | null;
  execution_ms: number;
  comp_count: number;
  lane_key: string;
  equipment: string;
  ts: string;
}

function telemetryEnabled(): boolean {
  try {
    if (import.meta.env.DEV) return true;
    if (typeof localStorage !== 'undefined' &&
        localStorage.getItem('rateGuardTelemetry') === '1') return true;
  } catch { /* ignore */ }
  return false;
}

export function emitRateGuardTelemetry(ev: RateGuardEvent): void {
  if (!telemetryEnabled()) return;
  // console.debug is filtered out of standard prod logs but visible in devtools
  // when the user opts in via localStorage.
  // eslint-disable-next-line no-console
  console.debug('[rate-guard]', ev);
}
